import { useEffect, useState } from 'react';

/**
 * Type representing a CSS module with a default export containing style classes
 */
type CSSModule = {
    default: { [key: string]: string };
    [key: string]: any;
};

export interface UseDynamicStylesOptions {
    /** Whether the alternate styles are currently active */
    isAlternateStylesActive?: boolean;
    /** Function that returns a promise for the default styles module */
    defaultStylesImporter: () => Promise<CSSModule>;
    /** Function that returns a promise for the alternate styles module */
    alternateStylesImporter: () => Promise<CSSModule>;
}

/**
 * A React hook that conditionally loads CSS modules based on application state.
 * This hook dynamically imports only the necessary CSS based on the `isAlternateStylesActive` flag.
 *
 * @param options - Configuration object for the hook
 * @returns The dynamically loaded style object
 */
export const useDynamicStyles = (
    options: UseDynamicStylesOptions,
): Record<string, string> => {
    const {
        isAlternateStylesActive = false,
        defaultStylesImporter,
        alternateStylesImporter,
    } = options;

    const [styles, setStyles] = useState<Record<string, string>>({});

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const importer = isAlternateStylesActive
                ? alternateStylesImporter
                : defaultStylesImporter;
            try {
                const module = await importer();
                const css = module?.default ?? module;
                if (!cancelled && typeof css === 'object' && css !== null) {
                    setStyles(css as Record<string, string>);
                }
            } catch {
                if (!cancelled) setStyles({});
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [
        isAlternateStylesActive,
        defaultStylesImporter,
        alternateStylesImporter,
    ]);

    return styles;
};
